import { Button } from "$components/button/button";
import { IconButton, styled, TextField } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import RemoveIcon from "@material-ui/icons/Remove";
import { useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  clampPercentageDelta,
  PERCENTAGE_DELTA_MAX,
  PERCENTAGE_DELTA_MIN,
  PERCENTAGE_DELTA_STEP,
} from "./bulk-percentage-adjust";

// Single horizontal bar by default; wraps to stacked rows when the
// viewport is too narrow to fit both groups side-by-side. The two child
// groups are atomic — they never split mid-group.
const Outer = styled("div")(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: theme.spacing(1, 2),
  width: "100%",
}));

const AdjustRow = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
}));

// Sits at the trailing edge of the bar; `marginLeft: auto` pushes it to
// the right on wide screens and lets it wrap to its own line, still
// right-aligned, on narrow ones.
const MainRow = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  marginLeft: "auto",
}));

// Mirror the table's inline number-cell styling so the % input matches
// the rest of the page visually: outlined, size="small", body2 font,
// centred value, tight padding. Width fits "-100" plus the % adornment
// without truncation.
const PercentageField = styled(TextField)(({ theme }) => ({
  width: "72px",
  "& .MuiOutlinedInput-root": {
    paddingLeft: 0,
    paddingRight: 0,
  },
  "& .MuiOutlinedInput-input": {
    textAlign: "center",
    padding: theme.spacing(0.5),
    fontSize: theme.typography.body2.fontSize,
    // Inputs under 16px make mobile browsers pinch-zoom the page on focus.
    [theme.breakpoints.down("xs")]: {
      fontSize: 16,
    },
  },
}));

// Compact icon-only steppers. 36px stays well above WCAG 2.5.8 (24px AA) and
// reads visually balanced next to the small input — the old 44px buttons
// dominated the row on narrow screens.
const StepButton = styled(IconButton)({
  padding: 6,
});

type Props = {
  changedCount: number;
  selectedCount: number;
  onApplyAdjustment: (percentageDelta: number) => void;
  onSave: () => void;
  onCancel: () => void;
  submitting: boolean;
};

export const PriceListEditModeBar: FC<Props> = ({
  changedCount,
  selectedCount,
  onApplyAdjustment,
  onSave,
  onCancel,
  submitting,
}) => {
  const { formatMessage } = useIntl();

  // Local-only percentage value. Nothing happens to the rows until Apply
  // is clicked — typing here is just composing the value to apply.
  const [percent, setPercent] = useState(0);
  const [draft, setDraft] = useState("0");
  const lastEmittedRef = useRef(0);
  useEffect(() => {
    if (lastEmittedRef.current !== percent) {
      setDraft(String(percent));
      lastEmittedRef.current = percent;
    }
  }, [percent]);

  const emit = (next: number) => {
    lastEmittedRef.current = next;
    setPercent(next);
  };

  const step = (direction: 1 | -1) => {
    const next = clampPercentageDelta(
      percent + direction * PERCENTAGE_DELTA_STEP,
    );
    setDraft(String(next));
    emit(next);
  };

  const showAdjust = selectedCount > 0;
  const applyDisabled = submitting || percent === 0;

  return (
    <Outer data-testid="priceList.editMode.bar">
      {showAdjust && (
        <AdjustRow data-testid="priceList.editMode.adjustRow">
          <StepButton
            size="small"
            onClick={() => step(-1)}
            disabled={submitting || percent <= PERCENTAGE_DELTA_MIN}
            aria-label={formatMessage({
              id: "priceList.bulkAdjust.decrement",
              defaultMessage: "Decrease by 2.5%",
            })}
            data-testid="priceList.bulkAdjust.decrement"
          >
            <RemoveIcon fontSize="small" />
          </StepButton>
          <PercentageField
            variant="outlined"
            size="small"
            type="text"
            value={draft}
            onChange={(e) => {
              const next = e.target.value;
              if (!/^-?\d*\.?\d*$/.test(next)) return;
              const parsed = Number.parseFloat(next);
              if (Number.isFinite(parsed)) {
                const clamped = clampPercentageDelta(parsed);
                setDraft(clamped === parsed ? next : String(clamped));
                emit(clamped);
              } else {
                setDraft(next);
                if (next === "") emit(0);
              }
            }}
            onBlur={() => {
              if (Number.parseFloat(draft) !== percent) {
                setDraft(String(percent));
              }
            }}
            disabled={submitting}
            inputProps={{
              inputMode: "numeric",
              "aria-label": formatMessage({
                id: "priceList.bulkAdjust.percentage",
                defaultMessage: "Percentage adjustment",
              }),
              "data-testid": "priceList.bulkAdjust.input",
            }}
            InputProps={{
              endAdornment: <span style={{ paddingRight: 4 }}>%</span>,
            }}
          />
          <StepButton
            size="small"
            onClick={() => step(1)}
            disabled={submitting || percent >= PERCENTAGE_DELTA_MAX}
            aria-label={formatMessage({
              id: "priceList.bulkAdjust.increment",
              defaultMessage: "Increase by 2.5%",
            })}
            data-testid="priceList.bulkAdjust.increment"
          >
            <AddIcon fontSize="small" />
          </StepButton>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => {
              onApplyAdjustment(percent);
              // Reset back to 0 so the user can stack further adjustments
              // without having to undo their previous % entry first.
              emit(0);
              setDraft("0");
            }}
            disabled={applyDisabled}
            data-testid="priceList.bulkAdjust.apply"
          >
            <FormattedMessage
              id="priceList.bulkAdjust.apply"
              defaultMessage="Apply to {count, plural, one {# item} other {# items}}"
              values={{ count: selectedCount }}
            />
          </Button>
        </AdjustRow>
      )}
      <MainRow>
        <Button
          variant="outlined"
          color="primary"
          onClick={onCancel}
          disabled={submitting}
          data-testid="priceList.editMode.cancel"
        >
          <FormattedMessage
            id="priceList.editMode.cancel"
            defaultMessage="Cancel"
          />
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={onSave}
          loading={submitting}
          disabled={submitting || changedCount === 0}
          data-testid="priceList.editMode.save"
        >
          <span data-testid="priceList.editMode.changedCount">
            <FormattedMessage
              id="priceList.editMode.save"
              defaultMessage="{count, plural, =0 {Save} one {Save (#)} other {Save (#)}}"
              values={{ count: changedCount }}
            />
          </span>
        </Button>
      </MainRow>
    </Outer>
  );
};

export default PriceListEditModeBar;
